import React from 'react'
import classnames from 'classnames'

import { makeStyles, useTheme, useMediaQuery, Typography, Button, FilePreview, Grid } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { isEmpty as isHtmlEmpty } from '@paintscout/util/util'
import type { PresentationBlockProps } from '../../'
import compareBlockProps from '../../util/compareBlockProps'
import { markAsDocumentImage } from '../../util/markAsDocumentImage'
import { InlineEditableHtml, InlineEditableMedia, InlineEditableText } from '../../inlineEditable'
import { usePresentation } from '../../../context'

interface DocumentRow {
  title?: string
  content?: string
  image?: any
}

const useStyles = makeStyles(
  (theme: Theme) => {
    return {
      root: {},
      content: {},
      image: {
        '& img': {
          borderRadius: theme.spacing(0.5)
        }
      },
      button: {
        display: 'block',
        textDecoration: 'none !important' as any,
        transition: '0.5s all'
      },
      mediaColumn: {
        display: 'flex',
        flexDirection: 'column'
      },
      title: {}
    }
  },
  { name: 'DocumentsBlock' }
)

function stopPropagation(ev: React.MouseEvent) {
  ev.stopPropagation()
}

interface DocumentRowProps {
  row: DocumentRow
  index: number
  classes: Record<string, string>
  handleEditField: (field: string, content: any) => void
  isEditable: boolean
  isSettings: boolean
  presentationLibrary: any
  presetColors: string[]
  blockPrefix: string
}

// module-scope so the component type is stable across renders — declaring it inside
// DocumentsBlock would remount the row (and drop inline-editor focus) on every render
function DocumentRowItem(props: DocumentRowProps) {
  const {
    row,
    index,
    classes,
    handleEditField,
    isEditable,
    isSettings,
    presentationLibrary,
    presetColors,
    blockPrefix
  } = props

  return (
    <Grid container spacing={3}>
      {(row.image || isEditable) && (
        <Grid item md={3}>
          {isEditable || isSettings ? (
            <InlineEditableMedia
              classes={{ root: classes.image }}
              mediaOptionsProps={{
                blockPrefix: blockPrefix,
                fieldSuffix: `rows[${index}].image`
              }}
              presentationLibrary={presentationLibrary}
              file={markAsDocumentImage(row.image)}
              handleEdit={(file) => handleEditField(`fields.rows[${index}].image`, file)}
              isEditable={isEditable}
              isSettings={isSettings}
              placeholderButtonText={'Add Document'}
              filePreviewProps={{ showFirstPageOnly: true }}
            />
          ) : (
            row.image && (
              <div className={classes.mediaColumn}>
                <a
                  href={row.image.src}
                  target={`paintscout-document-${index}`}
                  rel="noopener noreferrer"
                  onClick={stopPropagation}
                >
                  <FilePreview file={row.image} classes={{ root: classes.image }} selectedPages={[0]} />
                </a>
                {row.image.src && (
                  <a
                    href={row.image.src}
                    className={classes.button}
                    target={`paintscout-document-${index}`}
                    rel="noopener noreferrer"
                    onClick={stopPropagation}
                  >
                    <Button variant="contained" className={classnames(classes.button, 'themeButton')} fullWidth>
                      View Document
                    </Button>
                  </a>
                )}
              </div>
            )
          )}
        </Grid>
      )}

      <Grid item md={9}>
        <Grid container spacing={3} alignItems="center">
          {(row.title || isEditable) && (
            <Grid item md={12}>
              {isEditable ? (
                <InlineEditableText
                  variant={'h3'}
                  isSubmitOnEnter={true}
                  handleEdit={(content) => handleEditField(`fields.rows[${index}].title`, content)}
                  placeholderButtonText={'Add Title'}
                  classes={{ root: classes.title }}
                >
                  {row.title}
                </InlineEditableText>
              ) : (
                <Typography variant={'h3'}>{row.title}</Typography>
              )}
            </Grid>
          )}
          {((row.content && !isHtmlEmpty(row.content)) || isEditable) && (
            <Grid item md={12}>
              <InlineEditableHtml
                isEditable={isEditable}
                isSettings={isSettings}
                handleEdit={(content) => handleEditField(`fields.rows[${index}].content`, content)}
                content={row.content}
                placeholderButtonText={'Add Text'}
                classes={{ root: classes.content }}
                presetColors={presetColors}
                fieldKey={`${blockPrefix}.fields.rows[${index}].content`}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

function DocumentsBlock(props: PresentationBlockProps) {
  const { isEditable, isSettings, presetColors, block, blockPrefix, setFieldValue, submitForm, WrapperComponent } =
    props
  const { presentationLibrary } = usePresentation()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const responsiveSpacing = isMobile ? 3 : 6

  const classes = useStyles(props)
  const { fields, title } = block
  const rows: DocumentRow[] = fields?.rows ?? []

  const handleEditField = (field: string, content: any) => {
    setFieldValue(`${blockPrefix}.${field}`, content)
    submitForm()
  }

  return (
    <WrapperComponent {...props}>
      <Grid container spacing={responsiveSpacing}>
        {(title || isEditable) && (
          <Grid item xs={12} md={12}>
            {isEditable ? (
              <InlineEditableText
                variant={'h2'}
                isSubmitOnEnter={true}
                handleEdit={(content) => handleEditField('title', content)}
                placeholderButtonText={'Add Title'}
                classes={{ root: classes.title }}
              >
                {title}
              </InlineEditableText>
            ) : (
              <Typography variant={'h2'}>{title}</Typography>
            )}
          </Grid>
        )}
        {rows.map((row, index) => (
          <Grid item xs={12} md={12} key={index}>
            <DocumentRowItem
              row={row}
              index={index}
              classes={classes}
              handleEditField={handleEditField}
              isEditable={isEditable}
              isSettings={isSettings}
              presentationLibrary={presentationLibrary}
              presetColors={presetColors}
              blockPrefix={blockPrefix}
            />
          </Grid>
        ))}
      </Grid>
    </WrapperComponent>
  )
}

export default React.memo(DocumentsBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
